// Columna comparativa del período anterior para los estados financieros.
//
// NIC 1.38 pide presentar los estados con la información del período anterior.
// Todo lo que sigue se apoya en UNA sola RPC —`cont_estado_comparado`— que llama
// al estado dos veces y empareja las cuentas por código; aquí no se recalcula
// nada, sólo se pinta. Por eso la misma tabla sirve para los cuatro estados,
// incluido el NIIF 18, donde los subtotales van intercalados entre categorías.
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { field, rpc, usd } from './shared';

export type EstadoComparable = 'situacion' | 'resultados' | 'niif18' | 'flujo';

export interface CmpValores {
  actual: number; anterior: number; variacion: number; variacion_pct: number | null;
}
export interface CmpLinea extends CmpValores { codigo: string; nombre: string }
export interface CmpBloque extends CmpValores {
  tipo: 'seccion' | 'total'; clave: string; titulo: string;
  lineas?: CmpLinea[]; fuerte?: boolean;
}
export interface Comparado {
  estado: EstadoComparable; modo: string;
  actual: { desde: string | null; hasta: string };
  anterior: { desde: string | null; hasta: string };
  sin_clasificar: number | null;
  bloques: CmpBloque[];
}

// '' = comparativo apagado. Las etiquetas dicen QUÉ se compara, no cómo se llama
// el modo por dentro: el contador piensa en «el año pasado», no en 'anio'.
export const MODOS: { v: string; label: string }[] = [
  { v: '', label: 'Sin comparativo' },
  { v: 'anio', label: 'Mismo período del año anterior' },
  { v: 'periodo', label: 'Período inmediato anterior' },
  { v: 'cierre', label: 'Ejercicio anterior completo' },
];

const MODO_KEY: Record<string, string> = { '': 'sin_comparativo', anio: 'anio', periodo: 'periodo', cierre: 'cierre' };

export function CompararSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('contabilidad');
  return (
    <label className="flex flex-col text-xs text-muted-foreground no-print">{t('comparativo.comparar_con')}
      <select className={`${field} w-64 mt-1`} value={value} onChange={(e) => onChange(e.target.value)}>
        {MODOS.map((m) => <option key={m.v} value={m.v}>{t(`comparativo.${MODO_KEY[m.v]}`, { defaultValue: m.label })}</option>)}
      </select>
    </label>
  );
}

export function useComparado(
  estado: EstadoComparable, modo: string,
  p: { hasta: string; desde?: string; proyecto?: string; centro?: string },
) {
  return useQuery<Comparado>({
    enabled: !!modo,
    queryKey: ['cont-cmp', estado, modo, p.hasta, p.desde, p.proyecto, p.centro],
    queryFn: () => rpc<Comparado>('cont_estado_comparado', {
      _estado: estado, _hasta: p.hasta, _desde: p.desde ?? null, _modo: modo,
      _proyecto: p.proyecto || null, _centro: p.centro || null,
    }),
  });
}

// El rótulo sale de las fechas que DEVOLVIÓ la RPC, nunca de recalcularlas aquí:
// así la columna no puede rotular un período distinto del que se consultó.
export const rotulo = (p: { desde: string | null; hasta: string }) =>
  p.desde ? i18n.t('contabilidad:comparativo.rotulo_rango', { desde: p.desde, hasta: p.hasta }) : i18n.t('comun.al', { ns: 'contabilidad', hasta: p.hasta });

const Pct = ({ v }: { v: number | null }) => (
  <span className={v === null ? 'text-muted-foreground' : v < 0 ? 'text-red-600' : ''}>
    {v === null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)} %`}
  </span>
);
const Var = ({ v }: { v: number }) => <span className={v < 0 ? 'text-red-600' : ''}>{usd(v)}</span>;

const Valores = ({ b, clase = '' }: { b: CmpValores; clase?: string }) => (
  <>
    <td className={`py-1 px-3 text-right tabular-nums ${clase}`}>{usd(b.actual)}</td>
    <td className={`py-1 px-3 text-right tabular-nums text-muted-foreground ${clase}`}>{usd(b.anterior)}</td>
    <td className={`py-1 px-3 text-right tabular-nums ${clase}`}><Var v={Number(b.variacion)} /></td>
    <td className={`py-1 px-3 text-right tabular-nums ${clase}`}><Pct v={b.variacion_pct === null ? null : Number(b.variacion_pct)} /></td>
  </>
);

export function TablaComparativa({ data, cargando }: { data?: Comparado; cargando?: boolean }) {
  const { t } = useTranslation('contabilidad');
  if (cargando) return <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  if (!data) return null;

  // Mismo criterio que la pantalla simple: en NIIF 18 la utilidad bruta sólo se
  // muestra cuando aporta algo distinto de la operativa.
  const bruta = data.bloques.find((b) => b.clave === 'utilidad_bruta');
  const operativa = data.bloques.find((b) => b.clave === 'utilidad_operativa');
  const ocultarBruta = !!bruta && !!operativa && Number(bruta.actual) === Number(operativa.actual);

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-xs text-muted-foreground">
          <th className="py-2 px-3 text-left w-24">{t('comun.codigo')}</th>
          <th className="py-2 px-3 text-left">{t('comun.cuenta')}</th>
          <th className="py-2 px-3 text-right whitespace-nowrap">{rotulo(data.actual)}</th>
          <th className="py-2 px-3 text-right whitespace-nowrap">{rotulo(data.anterior)}</th>
          <th className="py-2 px-3 text-right">{t('comparativo.variacion')}</th>
          <th className="py-2 px-3 text-right">%</th>
        </tr>
      </thead>
      <tbody>
        {data.bloques.map((b) => {
          if (b.clave === 'utilidad_bruta' && ocultarBruta) return null;

          if (b.tipo === 'total') {
            return (
              <tr key={b.clave} className={b.fuerte ? 'border-y-2 border-primary/60 font-bold bg-primary/5' : 'border-t font-semibold'}>
                <td className="py-2 px-3" colSpan={2}>{b.titulo}</td>
                <Valores b={b} clase="py-2" />
              </tr>
            );
          }

          // Una sección sin movimiento en NINGUNO de los dos períodos no aporta
          // nada; una con movimiento en uno solo sí, y por eso no se oculta.
          const lineas = b.lineas || [];
          if (!lineas.length && !Number(b.actual) && !Number(b.anterior)) return null;

          return (
            <Fragment key={b.clave}>
              <tr className="bg-muted/50 font-semibold">
                <td className="py-1.5 px-3" colSpan={2}>{b.titulo}</td>
                <Valores b={b} clase="py-1.5" />
              </tr>
              {lineas.map((r) => (
                <tr key={r.codigo} className="border-b last:border-0">
                  <td className="py-1 px-3 font-mono text-xs">{r.codigo}</td>
                  <td className="py-1 px-3">{r.nombre}</td>
                  <Valores b={r} />
                </tr>
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

// El CSV lleva las mismas seis columnas que la pantalla y los mismos rótulos de
// período: quien lo abra en Excel tiene que poder decir de cuándo es cada cifra.
export function csvComparativo(data: Comparado): { headers: string[]; filas: (string | number | null)[][] } {
  const tr = (k: string, o?: Record<string, unknown>) => i18n.t(k, { ns: 'contabilidad', ...o }) as string;
  const headers = [tr('comparativo.seccion'), tr('comun.codigo'), tr('comun.cuenta'), rotulo(data.actual), rotulo(data.anterior), tr('comparativo.variacion'), '%'];
  const filas: (string | number | null)[][] = [];
  for (const b of data.bloques) {
    if (b.tipo === 'total') {
      filas.push(['', '', b.titulo, b.actual, b.anterior, b.variacion, b.variacion_pct]);
      continue;
    }
    filas.push([b.titulo, '', tr('comparativo.total_titulo', { titulo: b.titulo }), b.actual, b.anterior, b.variacion, b.variacion_pct]);
    for (const r of b.lineas || []) {
      filas.push([b.titulo, r.codigo, r.nombre, r.actual, r.anterior, r.variacion, r.variacion_pct]);
    }
  }
  return { headers, filas };
}
